// noinspection HtmlUnknownAttribute
import { customElement, property, query, state } from 'lit/decorators.js';
// eslint-disable-next-line import/named
import {
  convertImageToAcceptedType,
  imageFileFromClipboardData,
  PASTE_TARGET_SELECTOR,
  readImageFromSystemClipboard,
  shouldClaimPaste
} from './form-input-image-clipboard';
import type { ContextMenuAction } from '../wm-context-menu';
import '../wm-context-menu';
import { fileToBase64 } from '../../blob-converters';
import { getAssetCdnPath } from '../../common/assets';
import { html, LitElement, nothing, TemplateResult } from 'lit';
import { information } from '../modal-option';
import { isEmptyOrSpace } from '../string-helper-functions';
import { isImageLink } from './is-image-link';
import { LangFormInputView } from '../../../../localization/webmodule-common/other/ui/templateresult/form-input-view-lang';

export class InputView extends LitElement {
  createRenderRoot() {
    return this;
  }

  render() {
    return html``;
  }

  attributeTrue(name: string) {
    if (!this.hasAttribute(name)) return false;
    else {
      const attr = this.getAttribute(name);
      return attr == undefined || attr !== 'false';
    }
  }
}

@customElement('bs-paginator')
export class Paginator extends InputView {
  index = 1;
  count = 1;

  static get properties() {
    return {
      index: { type: Number },
      count: { type: Number }
    };
  }

  render() {
    let currentPage = this.index;
    const pageCount = this.count;

    const visiblePages = 11;
    const pageDeductor = 5;
    let firstDisplayPage = currentPage - pageDeductor;
    if (firstDisplayPage < 1) firstDisplayPage = 1;
    let lastDisplayPage = firstDisplayPage + visiblePages - 1;
    if (lastDisplayPage > pageCount) lastDisplayPage = pageCount;

    if (lastDisplayPage === pageCount && lastDisplayPage - firstDisplayPage + 1 < visiblePages)
      firstDisplayPage = Math.max(1, lastDisplayPage - visiblePages + 1);
    if (firstDisplayPage === 1 && lastDisplayPage - firstDisplayPage + 1 < visiblePages)
      lastDisplayPage = Math.min(pageCount, firstDisplayPage + visiblePages - 1);

    const includeSkipToFirst = firstDisplayPage > 1;
    const includeSkipToLast = lastDisplayPage < pageCount - 1;

    const clickEvent = (e: Event) => {
      e.preventDefault();
      const strPage = (e.target as HTMLUListElement).textContent?.toLowerCase();
      if (strPage === LangFormInputView.prev().toLowerCase()) currentPage--;
      else if (strPage === LangFormInputView.next().toLowerCase()) currentPage++;
      else if (strPage === LangFormInputView.last().toLowerCase()) currentPage = pageCount;
      else if (strPage === LangFormInputView.first().toLowerCase()) currentPage = 1;
      else currentPage = parseInt(strPage ?? '1');

      //handle accidental overflows
      currentPage = Math.min(pageCount, Math.max(1, currentPage));
      const event = new CustomEvent('page-change', {
        detail: {
          index: currentPage - 1
        }
      });
      this.dispatchEvent(event);
    };

    const pages = (): TemplateResult[] => {
      const pageResult: TemplateResult[] = [];
      for (let i = firstDisplayPage; i <= lastDisplayPage; i++) {
        if (i === currentPage) {
          pageResult.push(
            html` <li class="page-item active">
              <span class="page-link">${i}</span>
            </li>`
          );
        } else {
          pageResult.push(
            html` <li class="page-item">
              <a class="page-link" href="#" @click=${clickEvent}>${i}</a>
            </li>`
          );
        }
      }
      return pageResult;
    };

    const skipFirstClass = `page-item ${!includeSkipToFirst ? 'disabled' : ''}`;
    const currentPageClass = `page-item ${currentPage === 1 ? 'disabled' : ''}`;
    const lastPageClass = `page-item ${currentPage === pageCount ? 'disabled' : ''}`;
    const skipToLastClass = `page-item ${!includeSkipToLast ? 'disabled' : ''}`;
    return html` <nav aria-label="Page navigation">
      <ul class="pagination pagination-sm justify-content-center">
        <li class=${skipFirstClass}>
          <a class="page-link" href="#" @click=${clickEvent}>${LangFormInputView.first()}</a>
        </li>
        <li class=${currentPageClass}>
          <a class="page-link" href="#" rel="prev" @click=${clickEvent}>${LangFormInputView.prev()}</a>
        </li>
        ${pages()}
        <li class=${lastPageClass}>
          <a class="page-link" href="#" rel="prev" @click=${clickEvent}>${LangFormInputView.next()}</a>
        </li>
        <li class=${skipToLastClass}>
          <a class="page-link" href="#" @click=${clickEvent}>${LangFormInputView.last()}</a>
        </li>
      </ul>
    </nav>`;
  }
}

@customElement('bs-form-image-upload')
export class FormInputImageFileUploadView extends InputView {
  required?: boolean = false;
  @query('input')
  fileElement?: HTMLInputElement;
  @property({ type: Boolean }) readonly = false;
  @property() defaultImage = getAssetCdnPath('/assets/images/transparent-pixel.gif');

  @property({ type: Boolean }) showFileInput = true;
  @property({ type: Boolean }) isThumbnail = false;
  @property({ type: Boolean }) resize = false;
  @property({ type: Number }) resizeHeight = 400;
  @property({ type: Number }) resizeWidth = 400;
  @property({ type: Number }) displayType = 0;
  @property() clickCaption = 'upload image';
  @property({ type: Array }) acceptedFiles = ['image/gif', 'image/jpeg', 'image/png', 'image/svg+xml'];
  @property({ type: Boolean }) canDelete = false;
  private fileValue?: Blob;
  // Whether the pointer is over this uploader; used only to decide which instance owns a paste.
  private pointerInside = false;

  static get properties() {
    return {
      'data-label': { type: String },
      value: { type: String },
      required: { type: Boolean }
    };
  }

  private _fileExtension?: string;

  get fileExtension() {
    return this._fileExtension;
  }

  @state() private _value: string | undefined;

  public get value(): string | undefined {
    return this._value;
  }

  public set value(value: string | undefined) {
    this._value = value;
  }

  get isValueChanged(): boolean {
    if (this.fileElement?.files) return this.fileElement?.files.length > 0;

    return false;
  }

  get isImage() {
    return (
      (this.acceptedFiles.some(x => x.startsWith('image/')) && isEmptyOrSpace(this.value)) ||
      (!isEmptyOrSpace(this.value) && isImageLink(this.value))
    );
  }

  async ClearFiles() {
    if (this.fileElement?.files) {
      this.fileElement.files = null;
      this.fileElement.value = '';
    }
  }

  async base64Value() {
    if (this.fileValue) {
      return await fileToBase64(this.fileValue);
    }
    return '';
  }

  connectedCallback(): void {
    super.connectedCallback();
    this.addEventListener('dragover', this.eventDragOver);
    this.addEventListener('drop', this.eventDrop);
    // Paste is caught on the document because the upload zone is not a text control and so does
    // not receive paste itself; eventPaste decides whether this instance owns the event.
    this.addEventListener('mouseenter', this.eventPointerEnter);
    this.addEventListener('mouseleave', this.eventPointerLeave);
    document.addEventListener('paste', this.eventPaste);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('dragover', this.eventDragOver);
    this.removeEventListener('drop', this.eventDrop);
    this.removeEventListener('mouseenter', this.eventPointerEnter);
    this.removeEventListener('mouseleave', this.eventPointerLeave);
    document.removeEventListener('paste', this.eventPaste);
    // mouseleave only fires on pointer movement, so a detach while hovered would otherwise leave
    // this true and re-arm the document listener with a stale flag on the next connect.
    this.pointerInside = false;
  }

  render() {
    const reqSpan = this.required ? html`<span class="text-danger">*</span>` : html``;

    const changeEvent = (e: Event) => {
      const files = this.fileElement?.files;
      if (files) void this.acceptFiles(files, e);
    };

    const deleteEvent = e => {
      e.stopImmediatePropagation();
      this.fileValue = undefined;
      this.value = undefined;
      const newEvent = new CustomEvent('webmodule-delete', {
        detail: e,
        bubbles: true,
        composed: true
      });
      this.dispatchEvent(newEvent);
    };

    const eventPickImage = (e: Event) => {
      e.stopImmediatePropagation();
      if (this.readonly) return;
      this.fileElement?.click();
    };
    const imgId = `${this.dataset.id}-img`;

    const showDelete = this.canDelete && !isEmptyOrSpace(this.value);

    const fileInputTemplate = html`<input
      id="${this.dataset.id}"
      class="form-control ${showDelete ? 'input-upload-delete' : ''}"
      type="file"
      accept=${this.acceptedFiles.join(',')}
      @change=${changeEvent}
      value=${this.value}
      ?disabled=${this.readonly}
      ?readonly=${this.readonly}
      ?hidden=${!this.showFileInput}
    />`;

    const deleteButtonTemplate = showDelete
      ? html` <webmodule-icon-button
          class="image-upload-delete"
          library="fa"
          name="trash-can"
          @click=${deleteEvent}
          aria-label="Delete Resouce Logo"
        ></webmodule-icon-button>`
      : html``;

    //this is the default header as used in the branding where you can pick and image
    //or drag and drop an image
    const header = this.showFileInput
      ? html` <div class="row mb-3 align-items-center form-col-item image-upload-field">
          <label for=${this.dataset.id} class="col-2 form-col-label">${this.dataset.label}:${reqSpan}</label>
          <div class="col-10 form-col-input">${fileInputTemplate}${deleteButtonTemplate}</div>
        </div>`
      : fileInputTemplate; //this is a hidden input

    const imgSrc = isEmptyOrSpace(this.value) ? this.defaultImage : this.value;

    const isDefaultImg = isEmptyOrSpace(this.value);

    const focusTabIndex = this.readonly ? -1 : 0;

    // An explicit bar of actions sits under the image so that BOTH ways of supplying one are
    // visible. Paste in particular is otherwise undiscoverable - nothing on screen can tell you a
    // keyboard shortcut exists - and clicking the image itself to open a file dialog made pasting
    // actively awkward, because clicking was also the only way to focus the zone.
    //
    // Readonly keeps the bar, always and even when it has nothing to say, so the tile holds its
    // height and its boundary lines up with the tile beside it. What goes in it is the caller's
    // clickCaption, which says WHY the field is not editable (the ETO editor sets "Supplier to
    // upload" for a dealer) - but only while the image is still missing, because once it has
    // arrived that caption describes work already done.
    const readonlyCaption = isDefaultImg && !isEmptyOrSpace(this.clickCaption) ? this.clickCaption : html``;
    const actionBarTemplate = this.readonly
      ? html`<div class="image-upload-actions image-upload-actions-readonly">${readonlyCaption}</div>`
      : html`
          <div class="image-upload-actions">
            <webmodule-button size="small" variant="default" @click=${eventPickImage}>
              <webmodule-icon library="fa" name="folder-open" slot="prefix"></webmodule-icon>
              ${LangFormInputView.chooseImage()}
            </webmodule-button>
            <webmodule-button size="small" variant="default" @click=${this.eventPasteButton}>
              <webmodule-icon library="fa" name="paste" slot="prefix"></webmodule-icon>
              ${LangFormInputView.pasteImage()}
            </webmodule-button>
          </div>
        `;

    // The same actions again, on right-click, plus a Copy the bar has no room for. A thumbnail is
    // exactly the place someone expects a right-click to offer copying, and offering the other two
    // there as well costs nothing.
    const contextMenuTemplate = html`<wm-context-menu
      .actions=${this.contextMenuActions(eventPickImage)}
      zone=".single-file-drop-container, .image-upload-wrapper"
    ></wm-context-menu>`;

    const imageTemplate = !this.isThumbnail
      ? //this is the original template as used on the branding page
        html`
          <div tabindex=${focusTabIndex} class="row mb-3 align-items-center form-col-item image-upload-image">
            <div class="col-10 offset-0 offset-sm-2 offset-md-3 offset-xl-2 form-col-input">
              <div class="image-upload-wrapper">
                <img
                  src=${imgSrc}
                  id=${imgId}
                  class="img-fluid "
                  alt="${LangFormInputView.uploadedFileThumbnailAlt()}"
                />
              </div>
              ${actionBarTemplate}
            </div>
          </div>
        `
      : //this is a new template for thumbnail
        // The tile is the paste target, so it is focusable and says so. A focusable thing with no
        // focus state is a secret: clicking the tile already armed Ctrl+V, but nothing on screen
        // told you, and the hint only appears once it holds focus so it is never in the way.
        html`
          <div
            class="single-file-drop-container"
            tabindex=${focusTabIndex}
            aria-label=${this.readonly ? nothing : LangFormInputView.pasteHint()}
          >
            <img
              src=${imgSrc}
              id=${imgId}
              class="img-fluid ${isDefaultImg ? 'img-default-upload' : ''}"
              alt="${LangFormInputView.customRequestThumbnailAlt()}"
            />
            <h2>${LangFormInputView.thumbnailLabel()}</h2>
            <div class="image-upload-icon">
              <i class="fa fa-solid fa-cloud-arrow-up"></i>
            </div>
            ${this.readonly
              ? nothing
              : html`<div class="image-upload-paste-hint">${LangFormInputView.pasteHint()}</div>`}
          </div>
          ${actionBarTemplate}
        `;

    return html` ${header} ${imageTemplate} ${contextMenuTemplate} `;
  }

  resizeImage(file: File, maxWidth: number, maxHeight: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.src = URL.createObjectURL(file);
      image.onload = () => {
        const width = image.width;
        const height = image.height;

        if (width <= maxWidth && height <= maxHeight) {
          resolve(file);
          return;
        }

        let newWidth;
        let newHeight;

        if (width > height) {
          newHeight = height * (maxWidth / width);
          newWidth = maxWidth;
        } else {
          newWidth = width * (maxHeight / height);
          newHeight = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = newWidth;
        canvas.height = newHeight;

        const context = canvas.getContext('2d');
        if (!context) {
          resolve(file);
          return;
        }

        context.drawImage(image, 0, 0, newWidth, newHeight);

        canvas.toBlob(resolve as BlobCallback, file.type);
      };
      image.onerror = reject;
    });
  }

  protected eventDragOver = (e: Event) => {
    e.preventDefault();
  };

  protected eventDrop = async (e: DragEvent) => {
    e.preventDefault();
    if (this.readonly) return;

    const rawFile = e.dataTransfer?.files?.item(0);
    if (rawFile) await this.acceptRawFile(rawFile, e);
  };

  /**
   * The "Paste" button. Ctrl+V cannot be triggered from a click, so this reads the clipboard
   * directly. That API is not available everywhere (see readImageFromSystemClipboard), so every
   * outcome tells the user what happened rather than appearing to do nothing.
   */
  protected eventPasteButton = async (e: Event) => {
    e.stopImmediatePropagation();
    if (this.readonly) return;

    // Both failures tell the user to press Ctrl+V instead, so the zone takes focus AFTER the dialog
    // has been dismissed - the dialog holds focus while it is open, and focusing beforehand would
    // leave the advice pointing at a control that no longer has it.
    const outcome = await readImageFromSystemClipboard();
    if (outcome.status === 'unavailable') {
      await information(LangFormInputView.clipboardUnavailable());
      this.focusDropZone();
      return;
    }
    if (outcome.status === 'no-image') {
      await information(LangFormInputView.clipboardHasNoImage());
      this.focusDropZone();
      return;
    }

    await this.acceptRawFile(outcome.file, e);
  };

  /**
   * What the right-click menu offers, which is only ever what the tile can actually do: nothing at
   * all when it is readonly, which leaves the browser's own menu alone (see wm-context-menu).
   *
   * There is deliberately no Copy. The control resizes an image on the way in and only the resized
   * version is ever kept, so a Copy could only hand back the thumbnail - never the original the
   * user supplied - and an action that quietly gives you a downscaled image is worse than no
   * action. The browser's own Copy image on the readonly tile does the same job just as well.
   */
  private contextMenuActions(pickImage: (e: Event) => void): ContextMenuAction[] {
    if (this.readonly) return [];

    return [
      { id: 'choose', label: LangFormInputView.chooseImage(), icon: 'folder-open', run: pickImage },
      { id: 'paste', label: LangFormInputView.pasteImage(), icon: 'paste', run: this.eventPasteButton }
    ];
  }

  /** The focusable element inside the render, whichever template drew it. */
  private focusDropZone() {
    this.querySelector<HTMLElement>('[tabindex]')?.focus();
  }

  protected eventPointerEnter = () => {
    this.pointerInside = true;
  };

  protected eventPointerLeave = () => {
    this.pointerInside = false;
  };

  protected eventPaste = async (e: ClipboardEvent) => {
    // defaultPrevented means another uploader already claimed this paste; only one may act.
    if (this.readonly || e.defaultPrevented) return;

    const active = document.activeElement;
    const claim = shouldClaimPaste({
      activeElement: active,
      focusInside: active === this || this.contains(active),
      pointerInside: this.pointerInside,
      focusedUploader: active?.closest?.(PASTE_TARGET_SELECTOR) ?? null,
      self: this
    });
    if (!claim) return;

    // Claimed, so this paste is ours whatever it turned out to hold. Doing nothing would read as a
    // broken tile rather than as the wrong thing being on the clipboard, so say which it was.
    e.preventDefault();

    const rawFile = imageFileFromClipboardData(e.clipboardData);
    if (!rawFile) {
      await information(LangFormInputView.pasteNotAnImage(this.acceptedFiles.join(', ')));
      return;
    }

    await this.acceptRawFile(rawFile, e);
  };

  /**
   * Shared tail for drop and paste: the user is handing over image content, not a file type, so
   * a type outside acceptedFiles (webp is the common case for both a browser-copied image and a
   * browser-dragged one) is re-encoded locally rather than rejected. Pick is unaffected - the file
   * input's accept attribute already restricts what the OS dialog offers.
   *
   * Anything that cannot be turned into an accepted type is reported and NOT committed to the
   * input: acceptFiles assigns the file before onPreview re-checks the type, so committing a
   * rejected file would leave isValueChanged true with no value behind it.
   */
  private async acceptRawFile(rawFile: File, source: Event) {
    const file = this.acceptedFiles.includes(rawFile.type)
      ? rawFile
      : await convertImageToAcceptedType(rawFile, this.acceptedFiles);
    if (!file) {
      await information(LangFormInputView.supportedFileTypes(this.acceptedFiles.join(', ')));
      return;
    }

    const transfer = new DataTransfer();
    transfer.items.add(file);
    await this.acceptFiles(transfer.files, source);
  }

  private async acceptFiles(files: FileList, source: Event) {
    if (!this.fileElement) return;
    this.fileElement.files = files;
    await this.onPreview(undefined);
    this.dispatchEvent(
      new CustomEvent('webmodule-change', {
        detail: source,
        bubbles: true,
        composed: true
      })
    );
  }

  /**
   * Used to display a preview of a selected image file
   * @param event
   */
  private async onPreview(event?: Event) {
    const target = event?.target as HTMLInputElement;
    const file = target?.files?.item(0) ?? this.fileElement?.files?.item(0);
    if (file) {
      const validImageTypes = this.acceptedFiles;
      if (validImageTypes.includes(file.type)) {
        this._fileExtension = file.name.substring(file.name.lastIndexOf('.'));
        this.fileValue =
          this.resize && isImageLink(file.name)
            ? await this.resizeImage(file, this.resizeWidth, this.resizeHeight)
            : (file as Blob);
        this.value = window.URL.createObjectURL(this.fileValue);
      } else await information(LangFormInputView.supportedFileTypes(this.acceptedFiles.join(', ')));
    }
  }
}
